import React, { useState } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/ContactUs.css';

function ContactUs() {
  const [state, handleSubmit] = useForm("xayrrwbe");
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!e.target.checkValidity()) {
      setSubmitted(true);
      return;
    }
    setSubmitted(false);
    await handleSubmit(e);
  };

  return (
    <div className="contact-us-wrapper">
      <Helmet>
        <title>Contact Us - Spardhaweb | Get in Touch</title>
        <meta 
          name="description" 
          content="Get in touch with Spardhaweb for any inquiries or support related to MPSC preparation. Our team is here to help with your questions and provide the information you need." 
        />
        <meta 
          name="keywords" 
          content="contact Spardhaweb, MPSC test series support, MPSC exam inquiries, contact us" 
        />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Contact Us - Spardhaweb" />
        <meta 
          property="og:description" 
          content="Get in touch with Spardhaweb for any inquiries or support related to MPSC preparation. Our team is here to help." 
        />
        <meta property="og:image" content={`${window.location.origin}/logo_.jpg`} />
        <meta property="og:url" content="https://spardhaweb.com/contact-us" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Spardhaweb" />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://spardhaweb.com/contact-us" />
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <div className="container">
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">Get in Touch</h1>
            <p className="contact-hero-subtitle">
              We're here to help and answer any questions you might have
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="contact-content-section">
        <div className="container">
          <div className="row">
            {/* Contact Info */}
            <div className="col-lg-5 mb-5 mb-lg-0">
              <div className="contact-info-wrapper">
                <h2 className="contact-section-title">
                  Let's <span className="brand-highlight">Connect</span>
                </h2>
                <p className="contact-intro-text">
                  Have questions about Spardhaweb? Whether you need clarification on our services, 
                  assistance with navigating our platform, or want to learn more about what we offer, 
                  we're here to help!
                </p>

                {/* Contact Cards */}
                <div className="contact-info-cards">
                  <div className="contact-info-card">
                    <div className="contact-info-icon">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div className="contact-info-content">
                      <h4 className="contact-info-title">Email Us</h4>
                      <p className="contact-info-detail">support@spardhaweb.com</p>
                    </div>
                  </div>

                  <div className="contact-info-card">
                    <div className="contact-info-icon">
                      <i className="fas fa-clock"></i>
                    </div>
                    <div className="contact-info-content">
                      <h4 className="contact-info-title">Response Time</h4>
                      <p className="contact-info-detail">Within 24-48 hours</p>
                    </div>
                  </div>

                  <div className="contact-info-card">
                    <div className="contact-info-icon">
                      <i className="fas fa-headset"></i>
                    </div>
                    <div className="contact-info-content">
                      <h4 className="contact-info-title">Support</h4>
                      <p className="contact-info-detail">Monday - Saturday, 9 AM - 6 PM</p>
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="contact-social">
                  <h4 className="contact-social-title">Follow Us</h4>
                  <div className="contact-social-links">
                    <a href="#" className="contact-social-link facebook" aria-label="Facebook">
                      <i className="fab fa-facebook-f"></i>
                    </a>
                    <a href="#" className="contact-social-link twitter" aria-label="Twitter">
                      <i className="fab fa-twitter"></i>
                    </a>
                    <a href="#" className="contact-social-link instagram" aria-label="Instagram">
                      <i className="fab fa-instagram"></i>
                    </a>
                    <a href="#" className="contact-social-link youtube" aria-label="YouTube">
                      <i className="fab fa-youtube"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-7">
              <div className="contact-form-wrapper">
                {state.succeeded ? (
                  <div className="success-message-wrapper">
                    <div className="success-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <h3 className="success-title">Thank You!</h3>
                    <p className="success-message">
                      Your message has been sent successfully. We'll get back to you soon!
                    </p>
                    <button 
                      className="btn-back-to-form"
                      onClick={() => window.location.reload()}
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="contact-form-title">Send us a Message</h3>
                    <p className="contact-form-subtitle">
                      Fill out the form below and we'll respond as soon as possible
                    </p>

                    {submitted && (
                      <div className="alert-message alert-error">
                        <i className="fas fa-exclamation-circle"></i>
                        <span>Please fill out all required fields correctly.</span>
                      </div>
                    )}

                    <form onSubmit={onSubmit} className="contact-form" noValidate>
                      <div className="form-group">
                        <label htmlFor="name" className="form-label">
                          Full Name <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <i className="fas fa-user input-icon"></i>
                          <input
                            id="name"
                            type="text"
                            name="name"
                            className="form-input"
                            placeholder="Enter your full name"
                            required
                          />
                        </div>
                        <ValidationError
                          prefix="Name"
                          field="name"
                          errors={state.errors}
                          className="error-message"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email" className="form-label">
                          Email Address <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <i className="fas fa-envelope input-icon"></i>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            className="form-input"
                            placeholder="Enter your email address"
                            required
                          />
                        </div>
                        <ValidationError
                          prefix="Email"
                          field="email"
                          errors={state.errors}
                          className="error-message"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="subject" className="form-label">
                          Subject <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <i className="fas fa-tag input-icon"></i>
                          <input
                            id="subject"
                            type="text"
                            name="subject"
                            className="form-input"
                            placeholder="What is this regarding?"
                            required
                          />
                        </div>
                        <ValidationError
                          prefix="Subject"
                          field="subject"
                          errors={state.errors}
                          className="error-message"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="message" className="form-label">
                          Message <span className="required">*</span>
                        </label>
                        <div className="input-wrapper">
                          <i className="fas fa-comment-dots input-icon textarea-icon"></i>
                          <textarea
                            id="message"
                            name="message"
                            className="form-input form-textarea"
                            rows="5"
                            placeholder="Write your message here..."
                            required
                          />
                        </div>
                        <ValidationError
                          prefix="Message"
                          field="message"
                          errors={state.errors}
                          className="error-message"
                        />
                      </div>

                      <button 
                        type="submit" 
                        disabled={state.submitting} 
                        className="btn-submit"
                      >
                        {state.submitting ? (
                          <>
                            <span className="spinner"></span>
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane"></i>
                            Send Message
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="contact-faq-section">
            <h2 className="contact-section-title text-center mb-5">
              Frequently Asked <span className="brand-highlight">Questions</span>
            </h2>
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="faq-card">
                  <div className="faq-icon">
                    <i className="fas fa-question-circle"></i>
                  </div>
                  <h4 className="faq-question">How do I access the test series?</h4>
                  <p className="faq-answer">
                    Simply navigate to the Test Series section from the main menu and select 
                    the test you want to attempt. All tests are free to access.
                  </p>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="faq-card">
                  <div className="faq-icon">
                    <i className="fas fa-question-circle"></i>
                  </div>
                  <h4 className="faq-question">Are the study materials free?</h4>
                  <p className="faq-answer">
                    Yes! All our study materials, including PDFs and notes, are completely 
                    free to download and use for your MPSC preparation.
                  </p>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="faq-card">
                  <div className="faq-icon">
                    <i className="fas fa-question-circle"></i>
                  </div>
                  <h4 className="faq-question">How often is content updated?</h4>
                  <p className="faq-answer">
                    We update our content daily with new current affairs, quizzes, and study 
                    materials to keep you up-to-date with the latest information.
                  </p>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="faq-card">
                  <div className="faq-icon">
                    <i className="fas fa-question-circle"></i>
                  </div>
                  <h4 className="faq-question">Can I suggest new features?</h4>
                  <p className="faq-answer">
                    Absolutely! We welcome your feedback and suggestions. Use the contact 
                    form above to share your ideas with us.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ContactUs;